import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Game } from '@/types/game';
import { toast } from 'sonner';

export interface TopGame {
  id: string;
  game_id: string;
  position: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  game?: Game;
}

export const useTopGames = () => {
  return useQuery({
    queryKey: ['top-games'],
    queryFn: async () => {
      console.log('Fetching top games...');
      
      try {
        const { data, error } = await supabase
          .from('top_games')
          .select(`
            *,
            games:game_id (
              id,
              title,
              description,
              thumbnail,
              category,
              featured,
              trending,
              is_new,
              hidden,
              rating,
              play_count,
              fake_play_count,
              created_at,
              updated_at,
              tags,
              game_url
            )
          `)
          .eq('is_active', true)
          .order('position', { ascending: true });

        if (error) {
          console.error('Top games fetch error:', error);
          throw error;
        }

        console.log('Top games fetched successfully:', data?.length || 0, 'games');

        return (data || []).map((item): Game => ({
          id: item.games.id,
          title: item.games.title,
          description: item.games.description || '',
          thumbnail: item.games.thumbnail || '',
          category: item.games.category,
          featured: item.games.featured || false,
          trending: item.games.trending || false,
          isNew: item.games.is_new || false,
          rating: Number(item.games.rating) || 0,
          playCount: item.games.play_count || 0,
          fakePlayCount: item.games.fake_play_count,
          createdAt: item.games.created_at,
          updatedAt: item.games.updated_at,
          tags: item.games.tags || [],
          embedType: 'iframe',
          gameUrl: item.games.game_url || '',
        }));
      } catch (err) {
        console.error('Error in top games query:', err);
        // Return empty array on error to prevent infinite loading
        return [];
      }
    },
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 2 * 60 * 1000, // 2 minutes
    retry: false, // Don't retry on error
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

export const useTopGamesAdmin = () => {
  const queryClient = useQueryClient();

  const { data: topGames, isLoading } = useQuery({
    queryKey: ['admin-top-games'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('top_games')
        .select(`
          *,
          games:game_id (
            id,
            title,
            thumbnail,
            category
          )
        `)
        .order('position', { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  const addTopGameMutation = useMutation({
    mutationFn: async ({ gameId, position }: { gameId: string; position: number }) => {
      const { error } = await supabase
        .from('top_games')
        .insert({
          game_id: gameId,
          position,
          is_active: true,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-top-games'] });
      queryClient.invalidateQueries({ queryKey: ['top-games'] });
      toast.success('Game added to top games');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to add game to top games');
    },
  });

  const removeTopGameMutation = useMutation({
    mutationFn: async (topGameId: string) => {
      const { error } = await supabase
        .from('top_games')
        .delete()
        .eq('id', topGameId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-top-games'] });
      queryClient.invalidateQueries({ queryKey: ['top-games'] });
      toast.success('Game removed from top games');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Failed to remove game from top games');
    },
  });

  const updatePositionMutation = useMutation({
    mutationFn: async ({ topGameId, newPosition }: { topGameId: string; newPosition: number }) => {
      // Get current game data
      const currentGame = topGames?.find(tg => tg.id === topGameId);
      if (!currentGame) throw new Error('Game not found');

      const currentPosition = currentGame.position;
      
      // If position hasn't changed, do nothing
      if (currentPosition === newPosition) return;

      // Get the game at the target position
      const targetGame = topGames?.find(tg => tg.position === newPosition);
      
      if (targetGame) {
        // Swap positions: first move target to temporary position
        const tempPosition = Math.max(...(topGames?.map(tg => tg.position) || [0])) + 1;
        
        // Move target game to temp position
        const { error: tempError } = await supabase
          .from('top_games')
          .update({ position: tempPosition })
          .eq('id', targetGame.id);
        
        if (tempError) throw tempError;
        
        // Move current game to target position
        const { error: currentError } = await supabase
          .from('top_games')
          .update({ position: newPosition })
          .eq('id', topGameId);
        
        if (currentError) throw currentError;
        
        // Move target game to current position
        const { error: finalError } = await supabase
          .from('top_games')
          .update({ position: currentPosition })
          .eq('id', targetGame.id);
        
        if (finalError) throw finalError;
      } else {
        // Direct position update if no conflict
        const { error } = await supabase
          .from('top_games')
          .update({ position: newPosition })
          .eq('id', topGameId);

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-top-games'] });
      queryClient.invalidateQueries({ queryKey: ['top-games'] });
      toast.success('Position updated');
    },
    onError: (error: any) => {
      console.error('Position update error:', error);
      toast.error('Failed to update position');
    },
  });

  return {
    topGames,
    isLoading,
    addTopGame: addTopGameMutation.mutate,
    removeTopGame: removeTopGameMutation.mutate,
    updatePosition: updatePositionMutation.mutate,
    isAdding: addTopGameMutation.isPending,
    isRemoving: removeTopGameMutation.isPending,
    isUpdating: updatePositionMutation.isPending,
  };
};
